import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useProject } from '../../data/queries/use-project';
import { describeStatusChangeError } from '../../data/writers/update-project-status';
import { PROJECT_STATUS_LABEL } from './status';
import { CapturesList } from '../captures/CapturesList';
import { reportBuilderPath } from '../reports/report-route';
import { savedReportsPath } from '../reports/saved/saved-report-route';
import type { ProjectStatus } from '../../data/database.types';
import './projects.css';
import { errorCodeOf } from '../../lib/error-classification';

/**
 * Project Home (design export S05) — the S-03 foundation.
 *
 * Shows the Project's real name, its real status, a back path to Projects, and
 * the three status choices.
 *
 * STATUS IS ONE TAP, NO CONFIRMATION (Jerry's decision, 2026-08-11). A direct,
 * human-initiated, reversible change: confirmation in this product is reserved
 * for AI-proposed writes and for removal. The write is still guarded against a
 * stale read at the database — that protection is independent of the UI, so
 * one-tap does not mean one-tap-and-hope.
 *
 * CAPTURE IS LIVE as of M3 / S-03-02, ASK SOL as of M9 / S09-04, and GENERATE
 * REPORT as of M10 / S10-01 — so the locked layout now has no "Coming later"
 * placeholder left in it. **Nothing here is tappable-but-inert**, which is the
 * M1 rule of 2026-08-10 and the reason each entry only became a link when the
 * screen behind it existed.
 *
 * CAPTURE ELIGIBILITY (D-015): ONGOING and FINISHED offer Capture; CLOSED does
 * not. **This gate is convenience, not enforcement.** `create_capture` refuses
 * a CLOSED Project with CL001 regardless of what any screen shows, which is
 * what makes the rule true for a stale tab, a queued retry, or a direct URL.
 */

const STATUS_ORDER: ProjectStatus[] = ['ongoing', 'finished', 'closed'];

export function ProjectHomeScreen() {
  const { projectId } = useParams<{ projectId: string }>();
  const { status, project, error, changeStatus } = useProject(projectId);
  const [pending, setPending] = useState<ProjectStatus | null>(null);
  const [changeError, setChangeError] = useState<string | null>(null);

  async function handleStatus(next: ProjectStatus) {
    // Guard runs before any await, so repeated taps dispatched in the same tick
    // cannot both reach the writer. Disabled buttons are the visible half.
    if (pending || !project || next === project.status) return;

    setPending(next);
    setChangeError(null);
    try {
      await changeStatus(next);
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : String(caught);
      // The hook has already re-read, so the chip below now shows real server
      // truth while this message explains why the tap did not take effect.
      setChangeError(describeStatusChangeError(message, errorCodeOf(caught) ?? undefined));
    } finally {
      setPending(null);
    }
  }

  return (
    <main className="cl-screen project-home">
      <p className="project-home-back">
        <Link className="cl-link" to="/">
          ‹ Projects
        </Link>
      </p>

      {status === 'loading' ? (
        <p className="projects-loading" role="status">
          Loading this Project…
        </p>
      ) : null}

      {status === 'not-found' ? (
        <section className="project-home-missing">
          <h1 className="cl-screen-title">Project not available</h1>
          <p className="projects-empty-body">
            This Project does not exist, or it is not yours. Go back to Projects and pick one from
            the list.
          </p>
        </section>
      ) : null}

      {status === 'failed' && error ? (
        <p className="cl-error" role="alert">
          {error}
        </p>
      ) : null}

      {status === 'ready' && project ? (
        <>
          <header className="project-home-header">
            <h1 className="project-home-name">{project.name}</h1>
            <span className={`status-chip status-chip--${project.status}`}>
              {PROJECT_STATUS_LABEL[project.status]}
            </span>
          </header>

          <section className="project-home-status" aria-labelledby="project-status-heading">
            <h2 id="project-status-heading" className="sheet-label">
              Status
            </h2>
            <div className="project-home-status-choices" role="group" aria-label="Project status">
              {STATUS_ORDER.map((option) => {
                const isCurrent = option === project.status;
                return (
                  <button
                    key={option}
                    type="button"
                    className={`status-choice status-choice--${option}${
                      isCurrent ? ' status-choice--current' : ''
                    }`}
                    aria-pressed={isCurrent}
                    disabled={pending !== null || isCurrent}
                    onClick={() => void handleStatus(option)}
                  >
                    {pending === option ? 'Saving…' : PROJECT_STATUS_LABEL[option]}
                  </button>
                );
              })}
            </div>

            {changeError ? (
              <p className="cl-error" role="alert">
                {changeError}
              </p>
            ) : null}
          </section>

          <section className="project-home-actions" aria-label="Project actions">
            {project.status === 'closed' ? (
              // Absent as a control, present as an explanation. A disabled
              // button with no reason reads as a bug; this says why.
              <p className="projects-empty-body project-home-capture-closed">
                This Project is closed, so new Captures cannot be added to it.
              </p>
            ) : (
              // The design export's original line, live at last: speaking is
              // the primary way in (S-03-03), and typing remains permanent
              // product surface on the Capture screen itself.
              <Link className="project-home-capture" to={`/projects/${project.id}/capture`}>
                <span className="project-home-capture-title">Capture</span>
                <span className="project-home-capture-sub">
                  Hold the phone up and speak &mdash; or type it.
                </span>
              </Link>
            )}
            <div className="project-home-secondary">
              {/* ASK SOL IS LIVE as of M9 / S09-04. Offered on every status,
                  including CLOSED: Sol reads, and a closed Project's Captures
                  are still his to read. Only WRITING is gated by status, and
                  Sol performs no writes at all in M9. */}
              <Link className="project-home-action" to={`/projects/${project.id}/sol`}>
                Ask Sol
              </Link>
              {/* GENERATE REPORT IS LIVE as of M10 / S10-01, and offered on
                  every status for the same reason Ask Sol is: reporting READS
                  the work, and a closed Project's Captures are still his to
                  report on. Only WRITING is gated by Project status, and this
                  slice writes nothing.

                  The path comes from `reportBuilderPath` rather than a literal,
                  so this entry and the route cannot drift — which is exactly
                  how a Sol list ended up with nothing to tap. */}
              <Link className="project-home-action" to={reportBuilderPath(project.id)}>
                Generate Report
              </Link>
              {/* SAVED REPORTS ARE LIVE as of M10 / S10-05, and offered on every
                  status for the same reason the two above are: reading history
                  is never gated by a Project's status. The path comes from
                  `savedReportsPath` rather than a literal, so this entry and
                  the route cannot drift. */}
              <Link className="project-home-action" to={savedReportsPath(project.id)}>
                Saved Reports
              </Link>
            </div>
          </section>

          <CapturesList projectId={project.id} />
        </>
      ) : null}
    </main>
  );
}
